import React from 'react';
import Layout from '../../../shared/components/Layout';
import MiniProgramQRCode from '../../../shared/components/MiniProgramQRCode';
import {
  Smartphone,
  Shield,
  FileText,
  BarChart3,
  ExternalLink,
  QrCode,
  Chrome,
} from 'lucide-react';

const PortalPage: React.FC = () => {
  const systems = [
    {
      title: '微信小程序',
      description: '跑者和私兔的主要服务平台，提供招募、订单、支付等核心功能',
      icon: Smartphone,
      color: 'from-green-500 to-emerald-500',
      links: [
        { label: '扫码体验', url: '#', type: 'qrcode' as const, external: false },
        { label: '功能介绍', url: '/about#miniapp', external: false, type: undefined },
      ],
      features: ['微信登录', '招募发布', '订单支付', '评价系统'],
    },
    {
      title: '管理后台',
      description: '平台运营管理系统，管理用户、订单、赛事等数据',
      icon: Shield,
      color: 'from-orange-500 to-red-500',
      links: [
        { label: '访问后台', url: 'https://runbuddy.ehinfo.com.cn/admin/', external: true, type: undefined },
        { label: '使用文档', url: '/docs/admin', external: false, type: undefined },
      ],
      features: ['用户管理', '订单管理', '数据统计', '权限控制'],
      credentials: {
        username: 'admin',
        note: '需要管理员权限',
      },
    },
    {
      title: 'API文档',
      description: '完整的RESTful API接口文档，支持第三方集成',
      icon: FileText,
      color: 'from-blue-500 to-indigo-500',
      links: [
        { label: '查看文档', url: 'https://runbuddy.ehinfo.com.cn/api/docs', external: true, type: undefined },
        { label: 'API示例', url: '/docs/api-examples', external: false, type: undefined },
      ],
      features: ['用户认证', '订单接口', '支付接口', 'WebHook'],
    },
    {
      title: '数据看板',
      description: '实时业务数据监控和分析，支持投资人查看',
      icon: BarChart3,
      color: 'from-purple-500 to-pink-500',
      links: [
        { label: '查看数据', url: '/dashboard', external: false, type: undefined },
        { label: '下载报告', url: '/reports', external: false, type: undefined },
      ],
      features: ['实时数据', '趋势分析', '财务报表', '用户画像'],
    },
  ];

  return (
    <Layout>
      <div className="bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                系统入口导航
              </h1>
              <p className="text-xl text-orange-100">
                跑伴平台各子系统统一入口，快速访问所需功能
              </p>
            </div>
          </div>
        </section>

        {/* Systems Grid */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {systems.map((system, index) => {
                  const Icon = system.icon;
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                    >
                      {/* Header */}
                      <div className={`bg-gradient-to-r ${system.color} p-6 text-white`}>
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold">{system.title}</h3>
                          </div>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <p className="text-gray-600 mb-6">{system.description}</p>

                        {/* Features */}
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3">核心功能</h4>
                          <div className="grid grid-cols-2 gap-2">
                            {system.features.map((feature, idx) => (
                              <div
                                key={idx}
                                className="flex items-center text-sm text-gray-600"
                              >
                                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full mr-2"></div>
                                {feature}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Credentials */}
                        {system.credentials && (
                          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="text-sm">
                              <div className="font-semibold text-gray-900 mb-1">
                                登录信息
                              </div>
                              <div className="text-gray-600">
                                用户名: <code className="bg-white px-2 py-1 rounded">{system.credentials.username}</code>
                              </div>
                              <div className="text-gray-500 text-xs mt-2">
                                {system.credentials.note}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Links */}
                        <div className="flex flex-col space-y-2">
                          {system.links.map((link, idx) => (
                            <a
                              key={idx}
                              href={link.url}
                              target={link.external ? '_blank' : undefined}
                              rel={link.external ? 'noopener noreferrer' : undefined}
                              className={`flex items-center justify-center px-4 py-3 rounded-lg font-medium transition-colors ${
                                idx === 0
                                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {link.type === 'qrcode' && <QrCode className="w-4 h-4 mr-2" />}
                              {link.external && !link.type && <ExternalLink className="w-4 h-4 mr-2" />}
                              {link.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Access */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">快速访问</h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <a
                  href="https://runbuddy.ehinfo.com.cn/admin/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <Chrome className="w-8 h-8 text-orange-500 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">管理后台</h3>
                  <p className="text-sm text-gray-600">运营管理系统</p>
                </a>

                <a
                  href="https://runbuddy.ehinfo.com.cn/api/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <FileText className="w-8 h-8 text-blue-500 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">API文档</h3>
                  <p className="text-sm text-gray-600">接口文档</p>
                </a>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6">
                  <Smartphone className="w-8 h-8 text-green-500 mb-3" />
                  <h3 className="font-bold text-gray-900 mb-2">小程序</h3>
                  <p className="text-sm text-gray-600">微信扫码体验</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mini Program QR Code */}
        <section className="py-16 bg-gradient-to-br from-orange-50 to-red-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-4">微信小程序体验</h2>
              <p className="text-center text-gray-600 mb-12">
                扫描下方二维码，立即体验跑伴平台完整功能
              </p>
              
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <MiniProgramQRCode size="large" showTitle={false} />
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold mb-4 text-gray-900">小程序功能</h3>
                    <ul className="space-y-3">
                      <li className="flex items-start">
                        <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                        <span className="text-gray-700">微信一键登录，无需注册</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                        <span className="text-gray-700">发布招募需求，寻找专业私兔</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                        <span className="text-gray-700">在线支付订单，安全便捷</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                          <span className="text-white text-xs">✓</span>
                        </div>
                        <span className="text-gray-700">实时消息通知，不错过任何动态</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-orange-100 rounded-lg p-4">
                    <p className="text-sm text-orange-800">
                      <strong>提示：</strong>如果扫码无法直接打开，可以在微信中搜索"跑伴"小程序
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Info */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">技术架构</h2>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-bold text-lg mb-4">前端技术</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• React 18 + TypeScript</li>
                      <li>• 微信小程序原生开发</li>
                      <li>• TailwindCSS + Vite</li>
                      <li>• 响应式设计</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-4">后端技术</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li>• Python + FastAPI</li>
                      <li>• PostgreSQL数据库</li>
                      <li>• JWT认证</li>
                      <li>• RESTful API</li>
                    </ul>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="font-bold text-lg mb-4">部署信息</h3>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">主域名:</span>
                      <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
                        runbuddy.ehinfo.com.cn
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-600">API地址:</span>
                      <code className="ml-2 bg-gray-100 px-2 py-1 rounded">
                        /api
                      </code>
                    </div>
                    <div>
                      <span className="text-gray-600">SSL证书:</span>
                      <span className="ml-2 text-green-600">✓ Let's Encrypt</span>
                    </div>
                    <div>
                      <span className="text-gray-600">服务器:</span>
                      <span className="ml-2 text-gray-900">云服务器</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default PortalPage;
